// src/pages/lists.ts —— 06 A-04 推荐用户 / A-05 用户消费明细 / A-06 佣金记录（只读页）；行字段按 backend agentq/portal.go json tag
import type { Dict } from '../../../shared/src/dc';
import { commissionRateText, fmtAmount, fmtOriginal, fmtPlain } from '../../../shared/src/money';
import { fmtDateTime } from '../../../shared/src/time';
import { homeOf } from '../domains/home';
import { str, type AgentCtx } from '../domains/types';
import type { CommissionRow, TxRow, UserRow } from '../types';
import type { ChipKind } from '../ui';
import type { DetailAction, PageDef } from './types';

// 卡侧金额（卡消费 / 交易额）：$ 两位 BigInt 截断展示（TD-814）；非法串不抛出整页，记日志后显示占位
export function usd(v: string | null): string {
  if (v === null || v === '') return '—';
  try {
    const s = fmtAmount(v, 2);
    return s.startsWith('-') ? `-$${s.slice(1)}` : `$${s}`;
  } catch (e) {
    console.warn('[gp:agent] 非法金额', v, e);
    return '—';
  }
}

// 佣金金额（及以 USDT 计的充值手续费基数）：「{金额} USDT」去尾零、不加 $（对客评审 2026-10-15 #26：钱包余额与佣金为 USDT，卡相关为 $）
export function usdt(v: string | null): string {
  if (v === null || v === '') return '—';
  try {
    return `${fmtPlain(v)} USDT`;
  } catch (e) {
    console.warn('[gp:agent] 非法金额', v, e);
    return '—';
  }
}

// 卡交易手续费：从钱包扣，单位 USDT；与 H5 交易手续费同口径（两位截断 + 千分位 +「 USDT」，user-h5 cards.ts usdtAmt）
export function feeUsdt(v: string | null): string {
  if (v === null || v === '') return '—';
  try {
    return `${fmtAmount(v, 2)} USDT`;
  } catch (e) {
    console.warn('[gp:agent] 非法手续费', v, e);
    return '—';
  }
}

export function tzOf(ctx: AgentCtx): string | undefined {
  return ctx.s.sh.config?.timezone;
}

export function when(ctx: AgentCtx, iso: string | null): string {
  return fmtDateTime(iso, { tz: tzOf(ctx) });
}

// telegram_username 存库不带 @（与 H5 TG 展示同口径）；已带 @ 的原样
function tgName(v: string | null): string {
  if (!v) return '—';
  return v.startsWith('@') ? v : `@${v}`;
}

const KYC: Readonly<Record<UserRow['kyc_status'], readonly [ChipKind, string]>> = {
  none: ['gray', 'stKycNone'],
  pending: ['amber', 'stPending'],
  passed: ['green', 'stPass'],
  rejected: ['red', 'stKycRejected'],
};

// X-34：KYC 四态；枚举外取值按未认证兜底
function kycOf(v: string): readonly [ChipKind, string] {
  return v === 'none' || v === 'pending' || v === 'passed' || v === 'rejected' ? KYC[v] : KYC.none;
}

// D28：交易 status PascalCase 原样映射（8 态 + Unknown；MailCode 组本页不出现）
const TX_STATUS: Readonly<Record<string, string>> = {
  Settle: 'stSettle',
  Authorize: 'stAuthorize',
  AuthorizeDeclined: 'stDeclined',
  Auth_Delete: 'stAuthDel',
  Refund: 'stRefund',
  RefundCancell: 'stRefundCancel',
  Dispute: 'stDispute',
};

// 06 A-04「来源」：直推 或 `AG-xxx · {下级名}`（后端 source = 下级 biz_no，source_name = 下级名）
function sourceOf(t: Dict, r: UserRow): string {
  if (r.source === 'direct') return str(t, 'direct');
  return r.source_name ? `${r.source} · ${r.source_name}` : r.source;
}

// 06 A-06「来源」：直推 或 下级单号（不显示下级名称）
function viaOf(t: Dict, v: string): string {
  return v === 'direct' ? str(t, 'direct') : v;
}

function closeBtn(t: Dict): DetailAction {
  return { label: str(t, 'aDetail'), kind: 'primary', run: (ctx) => ctx.host.setState({ modal: null }) };
}

function typeKey(kind: CommissionRow['kind']): string {
  return kind === 'open' ? 'typeOpen' : kind === 'fx_spread' ? 'typeFXSpread' : 'typeSpend';
}

// rate_text 为落账时快照：新快照开卡「0.6 USDT」原样，旧快照「$0.60」/「$0.6−$0.2」兼容转为 USDT（对客评审 #26），充值「10%」原样
function rateOf(r: CommissionRow): string {
  return commissionRateText(r.rate_text);
}

// X-07 / D33：开卡佣金基数恒为 1 张；充值佣金基数 = 入金手续费（钱包 USDT，非卡侧金额）
function baseText(t: Dict, r: CommissionRow): string {
  return r.kind === 'open' ? `1 ${str(t, 'unitCard')}` : usdt(r.base_amount);
}

export const usersPage: PageDef<UserRow> = {
  page: 'users',
  route: 'GET /api/agent/users',
  echo: '/agent/users',
  csvName: 'users',
  tabs: ['all', 'verified', 'pending', 'no-card'],
  tabLabelKey: 'tabUsers',
  tabParam: 'tab',
  cols: [{ label: 'cUser' }, { label: 'cRegTime', sortKey: 'created_at' }, { label: 'cKyc', facet: 'kyc_status' }, { label: 'cCards' }, { label: 'cSpend' }, { label: 'cMyCommission' }, { label: 'cFrom' }],
  cells(ctx, r) {
    const t = ctx.t;
    const [kind, key] = kycOf(r.kyc_status);
    return [
      { v: r.email ?? str(t, 'tgUser'), sub: `${r.uid} · ${tgName(r.telegram_username)}` },
      { v: when(ctx, r.created_at) },
      { v: str(t, key), chip: kind },
      { v: String(r.card_count) },
      { v: usd(r.spent_total) },
      { v: usdt(r.my_commission) },
      { v: sourceOf(t, r) },
    ];
  },
  detail(ctx, r) {
    const t = ctx.t;
    return {
      title: r.email ?? r.uid,
      fields: [
        { k: 'UID', v: r.uid },
        { k: 'Telegram', v: tgName(r.telegram_username) },
        { k: str(t, 'cKyc'), v: str(t, kycOf(r.kyc_status)[1]) },
        { k: str(t, 'cCards'), v: String(r.card_count) },
        { k: str(t, 'cSpend'), v: usd(r.spent_total) },
        { k: str(t, 'cMyCommission'), v: usdt(r.my_commission) },
        { k: str(t, 'cFrom'), v: sourceOf(t, r) },
        { k: str(t, 'cRegTime'), v: when(ctx, r.created_at) },
      ],
      actions: [closeBtn(t)],
    };
  },
  // 06 A-04 统计卡：仅「我的用户总数」= 列表 total（GAP-19）
  stats: (ctx, total) => [{ label: str(ctx.t, 'kUsers'), value: String(total), sub: str(ctx.t, 'users'), dark: true }],
  facetLabel: (t, _param, v) => str(t, kycOf(v)[1]),
};

// 外币消费原币金额（「176,000 VND」）：amount 已是卡币种金额（2026-10-19）
const origOf = (r: TxRow): string => fmtOriginal(r.original_amount, r.original_currency);

export const spendPage: PageDef<TxRow> = {
  page: 'spend',
  route: 'GET /api/agent/user-transactions',
  echo: '/agent/user-transactions',
  csvName: 'tx',
  tabs: ['all', 'settle', 'refund', 'declined'],
  tabLabelKey: 'tabSpend',
  tabParam: 'tab',
  // D10：移除「计佣费率 / 我的佣金」两列，余 6 列；无 facet
  cols: [{ label: 'cUser' }, { label: 'cCardNo' }, { label: 'cMerchant' }, { label: 'cAmount' }, { label: 'cFee' }, { label: 'cTime', sortKey: 'transaction_date' }],
  cells(ctx, r) {
    return [
      { v: r.email ?? '—', sub: r.tx_no },
      { v: r.card_no, mono: true },
      { v: r.merchant ?? '—' },
      { v: usd(r.amount), sub: fmtOriginal(r.original_amount, r.original_currency) },
      { v: feeUsdt(r.fee) },
      { v: when(ctx, r.transaction_date) },
    ];
  },
  detail(ctx, r) {
    const t = ctx.t;
    return {
      title: r.merchant ?? r.tx_no,
      fields: [
        { k: str(t, 'cUser'), v: r.email ?? '—' },
        { k: str(t, 'cCardNo'), v: r.card_no },
        { k: str(t, 'cAmount'), v: origOf(r) ? `${usd(r.amount)} · ${origOf(r)}` : usd(r.amount) },
        { k: str(t, 'cFee'), v: feeUsdt(r.fee) },
        { k: str(t, 'cType'), v: '—' }, // 后端 TxRow 无 transaction_type（J-15）
        { k: str(t, 'cStatus2'), v: str(t, TX_STATUS[r.status] ?? 'stTxUnknown') },
      ],
      actions: [closeBtn(t)],
    };
  },
};

export const commissionsPage: PageDef<CommissionRow> = {
  page: 'commissions',
  route: 'GET /api/agent/commissions',
  echo: '/agent/commissions',
  csvName: 'comm',
  tabs: ['all', 'unsettled', 'settled', 'open', 'topup', 'fx_spread'],
  tabLabelKey: 'tabComm',
  tabParam: 'tab',
  cols: [
    { label: 'cId' }, { label: 'cSource' }, { label: 'cFrom' }, { label: 'cType', facet: 'type' }, { label: 'cBase' }, { label: 'cRate' }, { label: 'cAmount' },
    { label: 'cTime', sortKey: 'created_at' }, { label: 'cSettle', facet: 'settle_status' },
  ],
  cells(ctx, r) {
    const t = ctx.t;
    return [
      { v: r.biz_no, mono: true },
      { v: r.source_email ?? '—' },
      { v: viaOf(t, r.via) },
      { v: str(t, typeKey(r.kind)), chip: r.kind === 'open' ? 'purple' : 'blue' },
      { v: baseText(t, r) },
      { v: rateOf(r) },
      { v: usdt(r.amount) },
      { v: when(ctx, r.created_at) },
      r.status === 'settled' ? { v: str(t, 'stSettled'), chip: 'green' } : { v: str(t, 'stUnsettled'), chip: 'amber' },
    ];
  },
  detail(ctx, r) {
    const t = ctx.t;
    return {
      title: `${r.biz_no} · ${usdt(r.amount)}`,
      fields: [
        { k: str(t, 'cSource'), v: r.source_email ?? '—' },
        { k: str(t, 'cFrom'), v: viaOf(t, r.via) },
        { k: str(t, 'cType'), v: str(t, typeKey(r.kind)) },
        { k: str(t, 'cBase'), v: baseText(t, r) },
        { k: str(t, 'cRate'), v: rateOf(r) },
        { k: str(t, 'cSettle'), v: str(t, r.status === 'settled' ? 'stSettled' : 'stUnsettled') },
        { k: str(t, 'cTime'), v: when(ctx, r.created_at), wide: true },
      ],
      // 06 A-06：「提佣申请」绿色主按钮 → 关闭弹层并跳转 A-07
      actions: [{ label: str(t, 'withdraw'), kind: 'green', run: (c) => c.host.setState({ modal: null, page: 'withdraw' }) }],
    };
  },
  // 06 A-06 统计卡：可提现 / 本月佣金取 /agent/overview kpi（GAP-28：待结算卡不展示）
  stats(ctx) {
    const k = homeOf(ctx.s).overview?.kpi;
    return [
      { label: str(ctx.t, 'kAvailable'), value: k ? usdt(k.withdrawable) : '—', sub: str(ctx.t, 'withdraw'), dark: true },
      { label: str(ctx.t, 'kMonth'), value: k ? usdt(k.month_commission) : '—', sub: '', dark: false },
    ];
  },
  facetLabel(t, param, v) {
    if (param === 'type') return str(t, v === 'open' ? 'typeOpen' : 'typeSpend');
    return str(t, v === 'settled' ? 'stSettled' : 'stUnsettled');
  },
};
